export type PublicPricingTierId = "personal" | "plus" | "pro";
export type LegacyPricingTierId = "family" | "byo";
export type PricingTierId = PublicPricingTierId | LegacyPricingTierId;

export interface PricingTier {
  id: PricingTierId;
  name: string;
  monthlyPrice: number;
  tagline: string;
  bestFor: string;
  highlights: string[];
  featured?: boolean;
}

export const ANNUAL_DISCOUNT_PERCENT = 20;

export const MAIN_TIERS: PricingTier[] = [
  {
    id: "personal",
    name: "Personal",
    monthlyPrice: 12.99,
    tagline: "Your everyday AI companion",
    bestFor: "Best for individuals",
    highlights: [
      "Included AI usage",
      "All messaging channels",
      "Voice notes",
      "Reminders & scheduling",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    monthlyPrice: 19.99,
    tagline: "More power, more context",
    bestFor: "Best for busy professionals",
    highlights: [
      "Included AI usage",
      "All messaging channels",
      "Priority processing",
      "Advanced reminders",
      "Usage analytics",
    ],
    featured: true,
  },
  {
    id: "pro",
    name: "Pro",
    monthlyPrice: 29.99,
    tagline: "Maximum capability, no limits",
    bestFor: "Best for power users",
    highlights: [
      "Included AI usage",
      "All messaging channels",
      "Priority processing",
      "Dedicated support",
      "Advanced analytics",
    ],
  },
];

export const LEGACY_TIERS: PricingTier[] = [
  {
    id: "family",
    name: "Family",
    monthlyPrice: 24.99,
    tagline: "One assistant, whole household",
    bestFor: "Best for families",
    highlights: [
      "Included AI usage",
      "All messaging channels",
      "Shared inbox routing",
      "Extended memory",
      "Up to 4 members",
    ],
  },
  {
    id: "byo",
    name: "BYO Key",
    monthlyPrice: 9.99,
    tagline: "Bring your own AI keys for unlimited usage",
    bestFor: "Best for developers & tinkerers",
    highlights: [
      "Unlimited AI usage",
      "All messaging channels",
      "Use your own OpenRouter / API keys",
      "Platform updates included",
    ],
  },
];

/** Publicly purchasable tiers. */
export const PRICING_TIERS: PricingTier[] = [...MAIN_TIERS];

/** Legacy tiers retained for existing subscriptions and historical data. */
export const ALL_PRICING_TIERS: PricingTier[] = [...MAIN_TIERS, ...LEGACY_TIERS];

export const ANNUAL_TOTAL_BY_TIER: Record<PublicPricingTierId, number> = {
  personal: 124.7,
  plus: 191.9,
  pro: 287.9,
};

export const TIER_LABELS: Record<PricingTierId, string> = {
  personal: "Personal",
  plus: "Plus",
  pro: "Pro",
  family: "Family",
  byo: "BYO Key",
};

export function applyAnnualDiscount(monthlyPrice: number): number {
  return Number((monthlyPrice * (1 - ANNUAL_DISCOUNT_PERCENT / 100)).toFixed(2));
}

export function getAnnualTotalForTier(tier: PublicPricingTierId): number {
  return ANNUAL_TOTAL_BY_TIER[tier];
}

export function getTierLabel(tier: string | null | undefined): string {
  if (!tier) return "Trial";
  return TIER_LABELS[tier as PricingTierId] ?? tier.toUpperCase();
}
